/**
 * Module-level modal stack for back-button interception.
 *
 * Modals register a close callback when they open. A global popstate listener
 * (registered BEFORE React Router's) intercepts the browser back button —
 * if a modal is open, it closes the topmost modal and uses history.forward()
 * to restore the URL without pushing extra entries.
 */

type ModalCloseCallback = () => void;

const modalStack: ModalCloseCallback[] = [];

/** Register a modal's close handler. Returns an unregister function. */
export const registerModalForBack = (onClose: ModalCloseCallback): (() => void) => {
  modalStack.push(onClose);
  return () => {
    const idx = modalStack.indexOf(onClose);
    if (idx !== -1) modalStack.splice(idx, 1);
  };
};

/** Returns true if any modal is registered (i.e. open). */
export const hasOpenModal = (): boolean => modalStack.length > 0;

// Flag to ignore the popstate triggered by history.forward()
let ignoreNextPop = false;

const handlePopState = (e: PopStateEvent) => {
  if (ignoreNextPop) {
    ignoreNextPop = false;
    e.stopImmediatePropagation();
    return;
  }

  if (modalStack.length === 0) return;

  // Stop the event from reaching React Router's listener
  e.stopImmediatePropagation();

  // Close the topmost modal
  const cb = modalStack.pop();
  if (cb) cb();

  // Go forward to restore the original URL.
  // This will fire another popstate which we ignore.
  ignoreNextPop = true;
  window.history.forward();
};

// Register ONCE, as early as possible (module load time).
// Using `capture: true` ensures this fires before React Router's listener.
window.addEventListener('popstate', handlePopState, { capture: true });
